// src/components/Footer.js
import React from "react";
import styled from "styled-components";
import {
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaInstagram,
  FaArrowUp,
} from "react-icons/fa";
import { withPublicUrl } from "../config";

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  padding: 4rem 2rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Logo = styled.img`
  width: 160px;
  height: auto;
  filter: brightness(0) invert(1);
`;

const CompanyDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #cbd5e1;
  max-width: 400px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #667eea, #764ba2);
    transform: translateY(-2px);
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FooterTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: white;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 30px;
    height: 2px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 1px;
  }

  @media (max-width: 768px) {
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const FooterLink = styled.a`
  color: #cbd5e1;
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: block;
  padding: 0.25rem 0;

  &:hover {
    color: #667eea;
    transform: translateX(5px);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: #cbd5e1;
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  padding-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: #94a3b8;
  font-size: 0.9rem;
`;

const BackToTop = styled.button`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateY(-2px);
  }
`;

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <CompanyInfo>
            <Logo src={withPublicUrl("/logo.png")} alt="ISO Audit Pro - Global ISO 17025, ISO 17020 & AISC Accreditation Consulting Services" />
            <CompanyDescription>
              Your trusted international partner in ISO 17025, ISO 17020, and AISC 
              accreditation consulting. We help laboratories, inspection bodies, and 
              structural fabricators achieve compliance excellence worldwide.
            </CompanyDescription>
            <SocialLinks>
              <SocialLink href="#" aria-label="Facebook">
                <FaFacebook />
              </SocialLink>
              <SocialLink href="#" aria-label="Twitter">
                <FaTwitter />
              </SocialLink>
              <SocialLink href="#" aria-label="LinkedIn">
                <FaLinkedin />
              </SocialLink>
              <SocialLink href="#" aria-label="Instagram">
                <FaInstagram />
              </SocialLink>
            </SocialLinks>
          </CompanyInfo>

          <FooterSection>
            <FooterTitle>Services</FooterTitle>
            <FooterLink href="#services">ISO 17025 Testing Labs</FooterLink>
            <FooterLink href="#services">
              ISO 17020 Inspection Bodies
            </FooterLink>
            <FooterLink href="#services">AISC Structural Fabricators</FooterLink>
            <FooterLink href="#services">Training & Implementation</FooterLink>
            <FooterLink href="#services">International Compliance</FooterLink>
            <FooterLink href="#services">Audit & Assessment</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Company</FooterTitle>
            <FooterLink href="#about">About Us</FooterLink>
            <FooterLink href="#about">Our Mission</FooterLink>
            <FooterLink href="#about">Our Expertise</FooterLink>
            <FooterLink href="#about">Our Process</FooterLink>
            <FooterLink href="#contact">Contact Us</FooterLink>
            <FooterLink href="#">Careers</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Contact</FooterTitle>
            <ContactInfo>
              <ContactItem>📧 info@isoauditpro.com</ContactItem>
              <ContactItem>🌐 Serving Clients Worldwide</ContactItem>
              <ContactItem>
                🕐 24-48 Hour Response Time
                <br />
                International Support Available
              </ContactItem>
            </ContactInfo>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <Copyright>
            © 2025 ISOAuditPro. All rights reserved. | Privacy Policy | Terms of
            Service
          </Copyright>
          <BackToTop onClick={scrollToTop} aria-label="Back to top">
            <FaArrowUp />
          </BackToTop>
        </FooterBottom>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
